import "./Home.css";

import { Controller } from "@hotwired/stimulus";
import { openHomeSearchEventName, returnToHomeMapEventName } from "./HomeEvents";
import {
    getRecentHomeDestinations,
    type HomeRecentDestination,
    recordRecentHomeDestination
} from "./HomeRecentDestinations";
import type { ViewportBounds } from "../Shared/PermanentMap/SmartGuidePermanentMapTypes";
import { HorizontalRailScroller } from "../Shared/HorizontalRail/HorizontalRailDrag";
import { uiLanguage, webTranslations } from "../Shared/webTranslations";
import { localizeRegionDisplayNames, regionDisplayName } from "../Shared/RegionDisplayNames";
import {
    browserLocationChangedEventName,
    browserLocationFromEvent,
    readRecentBrowserLocation,
    type SmartGuideBrowserLocation
} from "../Shared/BrowserLocation/SmartGuideBrowserLocation";
import { smartGuideLocationBroker } from "../Shared/BrowserLocation/SmartGuideLocationBroker";

type HomeDestinationCard = {
    id: string;
    title: string;
    subtitle?: string | null;
    url: string;
    imageUrl?: string | null;
    countryCode?: string | null;
    kind: string;
    distanceMeters?: number | null;
    logoUrl?: string | null;
};

type HomeSearchResponse = {
    destinations: HomeDestinationCard[];
};

type ContinentMapEventDetail = {
    code: string;
    label: string;
    language: string;
    bounds: ViewportBounds;
};

type HomeLocationStatus = {
    message: string;
    instruction?: string;
    steps?: string[];
};

const geolocationPermissionDeniedCode = 1;

export default class HomeController extends Controller<HTMLElement> {
    static targets = [
        "searchForm",
        "searchInput",
        "searchClear",
        "searchPanel",
        "searchResults",
        "recentSection",
        "recentList",
        "locationResults",
        "locationStatus"
    ];

    static values = {
        searchUrl: String,
        locationUrl: String
    };

    declare readonly searchInputTarget: HTMLInputElement;
    declare readonly searchClearTarget: HTMLButtonElement;
    declare readonly searchPanelTarget: HTMLElement;
    declare readonly searchResultsTarget: HTMLElement;
    declare readonly recentSectionTarget: HTMLElement;
    declare readonly recentListTarget: HTMLElement;
    declare readonly locationResultsTarget: HTMLElement;
    declare readonly locationStatusTarget: HTMLElement;
    declare readonly hasLocationStatusTarget: boolean;
    declare readonly searchUrlValue: string;
    declare readonly locationUrlValue: string;

    private searchTimer: number | undefined;
    private lastSearchToken = 0;
    private locationRequestToken = 0;
    private locationSectionsLoaded = false;
    private locationSectionsRequestConsumed = false;
    private lastLocationErrorCode: number | undefined;
    private regionDisplayNamesObserver?: MutationObserver;
    private horizontalRailScroller?: HorizontalRailScroller;
    private get searchOpenedFromMap(): boolean {
        return this.element.dataset.homeSearchOpenedFromMap === "true";
    }

    private set searchOpenedFromMap(value: boolean) {
        if (value) {
            this.element.dataset.homeSearchOpenedFromMap = "true";
        } else {
            delete this.element.dataset.homeSearchOpenedFromMap;
        }
    }
    private readonly onOpenHomeSearch = (): void => {
        this.searchOpenedFromMap = true;
        this.setSearchActive(true);
        this.searchInputTarget.focus();
    };
    private readonly onBrowserLocationChanged = (event: Event): void => {
        const location = browserLocationFromEvent(event);
        if (!location || location.source !== "gps") {
            return;
        }

        this.loadLocationSectionsFromKnownLocation(location);
    };

    connect(): void {
        this.horizontalRailScroller = new HorizontalRailScroller({
            root: this.element,
            railSelector: ".home-horizontal-rail"
        });
        this.horizontalRailScroller.connect();
        document.addEventListener(browserLocationChangedEventName, this.onBrowserLocationChanged);
        document.addEventListener(openHomeSearchEventName, this.onOpenHomeSearch);
        this.renderRecentDestinations();
        localizeRegionDisplayNames(this.element, uiLanguage);
        this.observeRegionDisplayNames();
        this.loadLocationSections();
    }

    disconnect(): void {
        if (this.searchTimer !== undefined) {
            window.clearTimeout(this.searchTimer);
        }
        this.horizontalRailScroller?.disconnect();
        this.horizontalRailScroller = undefined;
        this.regionDisplayNamesObserver?.disconnect();
        this.regionDisplayNamesObserver = undefined;
        document.removeEventListener(browserLocationChangedEventName, this.onBrowserLocationChanged);
        document.removeEventListener(openHomeSearchEventName, this.onOpenHomeSearch);
    }

    submitSearch(event: Event): void {
        event.preventDefault();
        void this.searchNow();
    }

    searchInputChanged(): void {
        const query = this.searchInputTarget.value.trim();
        this.searchClearTarget.hidden = query.length === 0;

        if (this.searchTimer !== undefined) {
            window.clearTimeout(this.searchTimer);
        }

        if (query.length === 0) {
            this.setSearchActive(false);
            this.searchPanelTarget.hidden = true;
            this.searchResultsTarget.innerHTML = "";
            return;
        }

        this.setSearchActive(true);
        this.searchTimer = window.setTimeout(() => {
            void this.searchNow();
        }, 180);
    }

    clearSearch(): void {
        this.searchOpenedFromMap = false;
        this.searchInputTarget.value = "";
        this.searchClearTarget.hidden = true;
        this.setSearchActive(false);
        this.searchPanelTarget.hidden = true;
        this.searchResultsTarget.innerHTML = "";
        this.searchInputTarget.focus();
    }

    closeSearch(): void {
        const returnToMap = this.searchOpenedFromMap;
        this.clearSearch();
        if (returnToMap) {
            this.searchInputTarget.blur();
            document.dispatchEvent(new Event(returnToHomeMapEventName));
        }
    }

    recordRecentFromLink(event: Event): void {
        const link = event.currentTarget as HTMLElement;
        recordRecentHomeDestination({
            id: link.dataset.homeDestinationId ?? "",
            title: link.dataset.homeDestinationTitle ?? "",
            url: link.getAttribute("href") ?? "",
            imageUrl: link.dataset.homeDestinationImageUrl ?? null,
            kind: link.dataset.homeDestinationKind ?? null,
            logoUrl: link.dataset.homeDestinationLogoUrl ?? null
        });
    }

    openContinent(event: Event): void {
        const button = event.currentTarget as HTMLElement;
        const bounds = {
            west: parseFloat(button.dataset.homeContinentWest ?? ""),
            south: parseFloat(button.dataset.homeContinentSouth ?? ""),
            east: parseFloat(button.dataset.homeContinentEast ?? ""),
            north: parseFloat(button.dataset.homeContinentNorth ?? "")
        };

        if (!isUsableBounds(bounds)) {
            return;
        }

        const detail: ContinentMapEventDetail = {
            code: button.dataset.homeContinentCode ?? "",
            label: button.dataset.homeContinentLabel ?? "SmartGuide",
            language: uiLanguage,
            bounds
        };

        document.dispatchEvent(new CustomEvent<ContinentMapEventDetail>("smartguide:open-continent-map", { detail }));
    }

    retryLocation(event: Event): void {
        event.preventDefault();
        if (this.shouldReloadForLocationPermissionRetry()) {
            this.reloadPage();
            return;
        }

        if (this.hasLocationStatusTarget) {
            this.locationStatusTarget.hidden = true;
        }
        if (!this.locationSectionsLoaded) {
            this.locationSectionsRequestConsumed = false;
        }
        this.loadLocationSections();
    }

    private async searchNow(): Promise<void> {
        const query = this.searchInputTarget.value.trim();
        if (query.length === 0) {
            return;
        }

        const token = ++this.lastSearchToken;
        this.setSearchActive(true);
        this.searchPanelTarget.hidden = false;
        this.renderSearchStatus(webTranslations.searching);

        try {
            const url = new URL(this.searchUrlValue, window.location.origin);
            url.searchParams.set("query", query);

            const response = await fetch(url.toString(), { headers: { Accept: "application/json" } });
            if (!response.ok) {
                throw new Error(`Home search failed with ${response.status}`);
            }

            const result = await response.json() as HomeSearchResponse;
            if (token !== this.lastSearchToken) {
                return;
            }

            this.renderSearchResults(result.destinations ?? []);
        } catch {
            if (token === this.lastSearchToken) {
                this.renderSearchStatus(webTranslations.searchUnavailable);
            }
        }
    }

    private renderSearchResults(destinations: HomeDestinationCard[]): void {
        if (destinations.length === 0) {
            this.renderSearchStatus(webTranslations.noDestinationsFound);
            return;
        }

        this.searchResultsTarget.innerHTML = destinations
            .map(destination => this.renderSearchResult(destination))
            .join("");
    }

    private renderRecentDestinations(): void {
        const recentDestinations = getRecentHomeDestinations();
        if (recentDestinations.length === 0) {
            this.recentSectionTarget.hidden = true;
            this.recentListTarget.innerHTML = "";
            return;
        }

        this.recentSectionTarget.hidden = false;
        this.recentListTarget.innerHTML = recentDestinations
            .map(destination => this.renderRecentDestination(destination))
            .join("");
    }

    private loadLocationSections(): void {
        const recentLocation = readRecentBrowserLocation({ sources: ["gps"] });
        if (recentLocation) {
            this.loadLocationSectionsFromKnownLocation(recentLocation);
            return;
        }

        const requestToken = ++this.locationRequestToken;
        void smartGuideLocationBroker.requestFix("home-location", { profile: "balanced" })
            .then(browserLocation => {
                if (requestToken !== this.locationRequestToken) {
                    return;
                }

                this.lastLocationErrorCode = undefined;
                this.loadLocationSectionsFromKnownLocation(browserLocation);
            })
            .catch((error: GeolocationPositionError | Error) => {
                if (requestToken !== this.locationRequestToken || this.locationSectionsLoaded) {
                    return;
                }

                this.lastLocationErrorCode = "code" in error ? error.code : undefined;
                this.renderLocationStatus(this.buildLocationAccessStatus());
            });
    }

    private loadLocationSectionsFromKnownLocation(location: SmartGuideBrowserLocation): void {
        if (this.locationSectionsRequestConsumed || this.locationSectionsLoaded) {
            return;
        }

        this.locationSectionsRequestConsumed = true;
        const requestToken = ++this.locationRequestToken;
        this.lastLocationErrorCode = undefined;
        this.locationStatusTarget.hidden = true;
        void this.fetchLocationSections(location.latitude, location.longitude, requestToken);
    }

    private async fetchLocationSections(latitude: number, longitude: number, requestToken: number): Promise<void> {
        try {
            const url = new URL(this.locationUrlValue, window.location.origin);
            url.searchParams.set("latitude", latitude.toString());
            url.searchParams.set("longitude", longitude.toString());

            const response = await fetch(url.toString(), { headers: { Accept: "text/html" } });
            if (!response.ok) {
                throw new Error(`Home location failed with ${response.status}`);
            }

            const html = await response.text();
            if (requestToken !== this.locationRequestToken) {
                return;
            }

            this.renderLocationSections(html);
        } catch {
            if (requestToken !== this.locationRequestToken || this.locationSectionsLoaded) {
                return;
            }

            this.renderLocationStatus(webTranslations.nearbyDestinationsUnavailable);
        }
    }

    private renderLocationSections(html: string): void {
        this.locationSectionsLoaded = true;
        this.lastLocationErrorCode = undefined;
        this.locationStatusTarget.hidden = true;
        this.locationResultsTarget.innerHTML = html;
        localizeRegionDisplayNames(this.locationResultsTarget, uiLanguage);
        this.horizontalRailScroller?.disconnect();
        this.horizontalRailScroller = new HorizontalRailScroller({
            root: this.element,
            railSelector: ".home-horizontal-rail"
        });
        this.horizontalRailScroller.connect();
    }

    private observeRegionDisplayNames(): void {
        this.regionDisplayNamesObserver?.disconnect();
        this.regionDisplayNamesObserver = new MutationObserver(records => {
            records.forEach(record => {
                record.addedNodes.forEach(node => {
                    if (node instanceof HTMLElement) {
                        localizeRegionDisplayNames(node, uiLanguage);
                    }
                });
            });
        });
        this.regionDisplayNamesObserver.observe(this.element, { childList: true, subtree: true });
    }

    private renderLocationStatus(status: string | HomeLocationStatus): void {
        const content = typeof status === "string"
            ? { message: status }
            : status;
        const steps = content.steps?.length
            ? `<ol class="home-location__steps">${content.steps.map(step => `<li>${escapeHtml(step)}</li>`).join("")}</ol>`
            : "";
        const instruction = content.instruction
            ? `<span>${escapeHtml(content.instruction)}</span>`
            : "";
        this.locationStatusTarget.innerHTML = `
            <span class="home-location__message">
                <span>${escapeHtml(content.message)}</span>
                ${instruction}
                ${steps}
            </span>
            <button type="button" class="home-location__retry" data-action="home#retryLocation">${escapeHtml(webTranslations.retry)}</button>
        `;
        this.locationStatusTarget.hidden = false;
    }

    private buildLocationAccessStatus(): HomeLocationStatus {
        if (isFacebookInAppBrowser()) {
            return {
                message: webTranslations.facebookLocationRecovery,
                instruction: webTranslations.facebookLocationRecoveryInstruction
            };
        }

        const platform = detectMobilePlatform();
        if (platform === "ios") {
            const browser = detectBrowserName();
            return {
                message: webTranslations.allowLocationNearbyWithSteps,
                steps: [
                    `Settings > Privacy & Security > Location Services > ${browser === "Safari" ? "Safari Websites" : browser}`,
                    `Settings > Apps > ${browser} > Location`
                ]
            };
        }

        if (platform === "android") {
            const browser = detectBrowserName();
            return {
                message: webTranslations.allowLocationNearbyWithSteps,
                steps: [
                    `Settings > Location > App location permissions > ${browser}`,
                    `${browser} > Settings > Site settings > Location`
                ]
            };
        }

        if (platform === "mobile") {
            const browser = detectBrowserName();
            return {
                message: webTranslations.allowLocationNearbyWithSteps,
                steps: [
                    `Device Settings > Location > ${browser}`,
                    `${browser} > Site settings > Location`
                ]
            };
        }

        return {
            message: webTranslations.allowLocationNearby
        };
    }

    private renderSearchResult(destination: HomeDestinationCard): string {
        const countryLabel = formatCountryLabel(destination.countryCode, uiLanguage);

        return `
            <a class="home-search-result"
               href="${escapeAttribute(destination.url)}"
               data-turbo-frame="_top"
               data-action="home#recordRecentFromLink"
               data-home-destination-id="${escapeAttribute(destination.id)}"
               data-home-destination-title="${escapeAttribute(destination.title)}"
               data-home-destination-image-url="${escapeAttribute(destination.imageUrl ?? "")}"
               data-home-destination-logo-url="${escapeAttribute(destination.logoUrl ?? "")}"
               data-home-destination-kind="${escapeAttribute(destination.kind)}">
                ${this.renderImage(destination.imageUrl, destination.title, "home-search-result__image home-search-result__fallback")}
                <span class="home-search-result__body">
                    ${countryLabel ? `<span class="home-search-result__country">${escapeHtml(countryLabel)}</span>` : ""}
                    <span class="home-search-result__title">${escapeHtml(destination.title)}</span>
                    ${destination.subtitle ? `<span class="home-search-result__subtitle">${escapeHtml(destination.subtitle)}</span>` : ""}
                </span>
                <span class="home-search-result__chevron" aria-hidden="true">
                    <svg viewBox="0 0 24 24" focusable="false" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m9 5 7 7-7 7" /></svg>
                </span>
            </a>
        `;
    }

    private renderSearchStatus(message: string): void {
        const status = document.createElement("div");
        status.className = "home-search-result__empty";
        status.textContent = message;
        this.searchResultsTarget.replaceChildren(status);
    }

    private renderRecentDestination(destination: HomeRecentDestination): string {
        return `
            <a class="home-recent-card" href="${escapeAttribute(destination.url)}" data-turbo-frame="_top">
                <span class="home-recent-card__media">
                    ${this.renderImage(destination.imageUrl, destination.title, "home-recent-card__image home-recent-card__fallback")}
                    ${destination.logoUrl ? `<img class="home-recent-card__logo" src="${escapeAttribute(destination.logoUrl)}" alt="" loading="lazy" decoding="async" />` : ""}
                </span>
                <span class="home-recent-card__body">
                    <span class="home-recent-card__title">${escapeHtml(destination.title)}</span>
                </span>
            </a>
        `;
    }

    private renderImage(imageUrl: string | null | undefined, title: string, className: string): string {
        if (imageUrl) {
            return `<img class="${className}" src="${escapeAttribute(imageUrl)}" alt="" loading="lazy" decoding="async" />`;
        }

        return `<span class="${className}" aria-hidden="true">${escapeHtml(initials(title))}</span>`;
    }

    private setSearchActive(isActive: boolean): void {
        this.element.classList.toggle("home-page--searching", isActive);
    }

    private shouldReloadForLocationPermissionRetry(): boolean {
        return this.lastLocationErrorCode === geolocationPermissionDeniedCode &&
            detectMobilePlatform() === "ios";
    }

    private reloadPage(): void {
        window.location.reload();
    }

}

function isUsableBounds(bounds: ViewportBounds): boolean {
    return Number.isFinite(bounds.west) &&
        Number.isFinite(bounds.south) &&
        Number.isFinite(bounds.east) &&
        Number.isFinite(bounds.north) &&
        bounds.west < bounds.east &&
        bounds.south < bounds.north;
}

function detectMobilePlatform(): "ios" | "android" | "mobile" | "desktop" {
    const userAgent = navigator.userAgent || "";
    if (/iPad|iPhone|iPod/i.test(userAgent) ||
        (/Macintosh/i.test(userAgent) && (navigator.maxTouchPoints ?? 0) > 1)) {
        return "ios";
    }

    if (/Android/i.test(userAgent)) {
        return "android";
    }

    if (/Mobile|Mobi/i.test(userAgent)) {
        return "mobile";
    }

    return "desktop";
}

function isFacebookInAppBrowser(): boolean {
    return /FBAN\/|FBAV\/|FB_IAB\//i.test(navigator.userAgent || "");
}

function detectBrowserName(): string {
    const userAgent = navigator.userAgent || "";
    if (/CriOS|Chrome|Chromium/i.test(userAgent) && !/Edg|EdgiOS|EdgA|OPR|SamsungBrowser/i.test(userAgent)) {
        return "Chrome";
    }

    if (/FxiOS|Firefox/i.test(userAgent)) {
        return "Firefox";
    }

    if (/EdgiOS|EdgA|Edg/i.test(userAgent)) {
        return "Edge";
    }

    if (/SamsungBrowser/i.test(userAgent)) {
        return "Samsung Internet";
    }

    if (/OPiOS|OPR|Opera/i.test(userAgent)) {
        return "Opera";
    }

    if (/Safari/i.test(userAgent)) {
        return "Safari";
    }

    return webTranslations.yourBrowser;
}

function initials(title: string): string {
    return title
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase() ?? "")
        .join("");
}

function formatCountryLabel(countryCode: string | null | undefined, language: string): string {
    return regionDisplayName(countryCode, language).toLocaleUpperCase(language);
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function escapeAttribute(value: string): string {
    return escapeHtml(value);
}
